/**
 * IMPORTS
 */
import * as events from 'src/aggregates/preferences/events';


/**
 * TYPES
 */
import {IAppState} from 'src/aggregates/index.d';
import {IEvent} from 'src/aggregates/index.d';
import {IPreferencesAddAutoMessage} from 'src/aggregates/preferences/commands.d';
import {IPreferencesDisableTour} from 'src/aggregates/preferences/commands.d';
import {IPreferencesLoad} from 'src/aggregates/preferences/commands.d';
import {IPreferencesRemoveAutoMessage} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetCurrentAutoMessage} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetEmailPageSize} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetFeedback} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetTagColor} from 'src/aggregates/preferences/commands.d';
import {IPreferencesStart} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSwitchMuteNotifications} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSwitchPaused} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSwitchWalletReminder} from 'src/aggregates/preferences/commands.d';


/**
 * CONSTANTS AND DEFINITIONS
 */
const AUTOMATIC_MESSAGES_KEY = '@pecazap:automatic_messages';


/**
 * CODE
 */

/**
 * I am a preferences add auto message command handler.
 *
 * :param command: preferences add auto message command
 * :param state: application state
 *
 * :returns: events
 */
async function* addAutoMessage(
    command: IPreferencesAddAutoMessage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get auto messages ids as numbers
    const messagesId = Object.keys(
        state.preferences.byId[command._target].autoMessages.byId,
    ).map(key => Number(key));

    // get the last message id
    const lastMessageId = messagesId.reduce((prev, current) =>
    {
        // return biggest number
        return (current > prev) ? current : prev;
    }, 0);

    // return added auto message event
    yield events.preferencesAddedAutoMessage(
        command.message,
        lastMessageId + 1,
        command._target,
    );
}


/**
 * I am a preferences disable tour command handler.
 *
 * :param command: preferences disable tour command
 * :param state: application state
 *
 * :returns: events
 */
async function* disableTour(
    command: IPreferencesDisableTour,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // return feedback set event
    yield events.preferencesTourDisabled(command._target);
}


/**
 * I am a preferences feedback handler.
 *
 * :param command: preferences set feedback command
 * :param state: application state
 *
 * :returns: events
 */
async function* setFeedback(
    command: IPreferencesSetFeedback,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // return feedback set event
    yield events.preferencesFeedbackSet(
        command.feedback,
        command._target,
    );
}


/**
 * I am a preferences load command handler.
 *
 * :param command: preferences load command
 * :param state: application state
 *
 * :returns: events
 */
async function* load(
    command: IPreferencesLoad,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // search for old messages in the localStorage
    const oldMessages = JSON.parse(
        localStorage.getItem(AUTOMATIC_MESSAGES_KEY),
    );

    // there are old messages: add them to the store and delete this reference
    if (oldMessages !== undefined && oldMessages !== null)
    {

        // get key and text message values
        for (const [key, message] of Object.entries<string>(oldMessages))
        {
            // add message to redux with current id
            yield events.preferencesAddedAutoMessage(
                message,
                Number(key),
                command._target,
            );
        }

        // delete old localStorage reference
        localStorage.removeItem(AUTOMATIC_MESSAGES_KEY);
    }
}


/**
 * I am a preferences remove auto message command handler.
 *
 * :param command: preferences remove auto message command
 * :param state: application state
 *
 * :returns: events
 */
async function* removeAutoMessage(
    command: IPreferencesRemoveAutoMessage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get user preferences
    const preferences = state.preferences.byId[command._target];

    // deleted message is the current one: unselect it
    if (preferences.autoMessages.current === command.message)
    {
        yield events.preferencesSetCurrentAutoMessage(
            null,
            command._target,
        );
    }

    // return removed auto message event
    yield events.preferencesRemovedAutoMessage(
        command.message,
        command._target,
    );
}


/**
 * I am a preferences set current auto message command handler.
 *
 * :param command: preferences set current auto message command
 * :param state: application state
 *
 * :returns: events
 */
async function* setCurrentAutoMessage(
    command: IPreferencesSetCurrentAutoMessage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.preferencesSetCurrentAutoMessage(
        command.message,
        command._target,
    );
}


/**
 * I am a preferences set email page size command handler.
 *
 * :param command: preferences set email page size command
 * :param state: application state
 *
 * :returns: events
 */
async function* setEmailPageSize(
    command: IPreferencesSetEmailPageSize,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.preferencesEmailPageSizeSet(
        command.size,
        command._target,
    );
}


/**
 * I am a preferences update tag color command handler.
 *
 * :param command: preferences update tag color command
 * :param state: application state
 *
 * :returns: events
 */
async function* setTagColor(
    command: IPreferencesSetTagColor,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.preferencesTagColorSet(
        command.color,
        command.tag,
        command._target,
    );
}


/**
 * I am a preferences start command handler.
 *
 * :param command: preferences start command
 * :param state: application state
 *
 * :returns: events
 */
async function* start(
    command: IPreferencesStart,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.preferencesStarted(command._target);
}


/**
 * I am a preferences switch mute notifications command handler.
 *
 * :param command: preferences switch mute notifications command
 * :param state: application state
 *
 * :generates: events
 */
async function* switchMuteNotifications(
    command: IPreferencesSwitchMuteNotifications,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // return switch mute notifications event
    yield events.preferencesSwitchedMuteNotifications(
        command._target,
        command.muteNotifications,
    );
}


/**
 * I am a preferences switch paused command handler.
 *
 * :param command: preferences switch paused command
 * :param state: application state
 *
 * :generates: events
 */
 async function* switchPaused(
    command: IPreferencesSwitchPaused,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // return switch paused event
    yield events.preferencesSwitchedPaused(
        command._target,
        command.paused,
    );
}


/**
 * I am a preferences wallet reminder command handler.
 *
 * :param command: preferences switch wallet reminder command
 * :param state: application state
 *
 * :generates: events
 */
async function* switchWalletReminder(
    command: IPreferencesSwitchWalletReminder,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // return switched wallet reminder event
    yield events.preferencesSwitchedWalletReminder(
        command._target,
        command.walletReminder,
    );
}


/**
 * EXPORTS
 */
export {
    addAutoMessage,
    disableTour,
    load,
    removeAutoMessage,
    setCurrentAutoMessage,
    setEmailPageSize,
    setFeedback,
    setTagColor,
    start,
    switchMuteNotifications,
    switchPaused,
    switchWalletReminder,
};
